// Phase-A R2.8 — typed.
// =====================================================
// More pages: Calendar, Library, Help, Pricing, Faculty
// =====================================================

// =====================================================
// Calendar / Schedule
// =====================================================
import React from "react";
import { Icon } from "../icons";
import { toFa } from "../shared";
import { useAuth } from "../auth/AuthContext";
import {
  facultyDirectoryApi,
  plansApi,
  subscriptionsApi,
  libraryApi,
  calendarApi,
  registrationApi,
  sectionsApi,
} from "../api/endpoints.js";
import { HelpNote } from "../components/HelpNote";
import { PersonLink } from "../components/PersonLink";
import { BookmarkButton } from "../components/BookmarkButton";
import ModalShell from "../components/ModalShell";
import { JalaliCalendar } from "../components/JalaliCalendar";
import { JalaliDatePicker } from "../components/JalaliDatePicker";
import { formatJalaliShort, toFaDigits } from "../i18n/jalali";
import { useRole } from "../role";
import { Button } from "../ui";
import type { Go } from "../router";
// Phase-14.7 R2: sidebar + footer come from Layout (router.tsx). The
// per-page <Footer/> calls below are gone — CSS used to hide them on
// workspace routes; now we just don't render them.

interface MorePageProps { go: Go }

// =====================================================
// Calendar — REAL (R-CAL). Two real data sources, no mock, no «به‌زودی» stub:
//   (1) calendarApi.list() → institutional events (تحصیلی/تعطیلی/امتحان/رویداد/مهلت)
//       rendered in a Jalali month grid (JalaliCalendar) + a selected-day list,
//       with a category legend. Staff (admin/super_admin/faculty_admin) get a real
//       ModalShell create form (and per-event ویرایش/حذف); non-staff just view.
//   (2) the user's REAL weekly class schedule — students read registrationApi.me()
//       (their enrolled sections), instructors read sectionsApi.mine() (their
//       teaching load); both carry the section `schedule` [{day,start,end}] in
//       minutes, rendered as a شنبه..جمعه weekday grid (minutes→HH:MM, Persian
//       digits). Honest loading/empty/error states throughout.
// =====================================================

type CalCategory = "academic" | "holiday" | "exam" | "event" | "deadline";

interface CalEvent {
  id: string;
  title: string;
  description: string | null;
  category: CalCategory | string;
  startDate: string; // YYYY-MM-DD
  endDate: string | null;
  audience: string;
  createdBy: string;
}

interface CalCategoryMeta {
  label: string;
  color: string;
  icon: string;
}
const CAL_CAT_META: Record<CalCategory, CalCategoryMeta> = {
  academic: { label: "آموزشی", color: "#0ea5e9", icon: "book" },
  holiday: { label: "تعطیلی", color: "#ef4444", icon: "star" },
  exam: { label: "آزمون", color: "#f59e0b", icon: "file" },
  event: { label: "رویداد", color: "#8b5cf6", icon: "calendar" },
  deadline: { label: "مهلت", color: "#e11d48", icon: "bell" },
};
const CAL_CATEGORIES: CalCategory[] = ["academic", "holiday", "exam", "event", "deadline"];
const calMetaFor = (c: string): CalCategoryMeta =>
  CAL_CAT_META[c as CalCategory] ?? { label: c, color: "var(--fg-mute)", icon: "calendar" };
const calColorFor = (c: string): string => calMetaFor(c).color;

const STAFF_ROLES: ReadonlyArray<string> = ["admin", "super_admin", "faculty_admin"];

// Weekday labels — شنبه..جمعه (0..6), matching the section schedule's day index.
const WEEKDAYS_FA = ["شنبه", "یک‌شنبه", "دوشنبه", "سه‌شنبه", "چهارشنبه", "پنج‌شنبه", "جمعه"];
const minToHHMM = (min: number): string =>
  toFaDigits(`${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`);
// JS getDay(): 0=Sun..6=Sat → Persian week index where 0=شنبه..6=جمعه.
const persianTodayIndex = (): number => (new Date().getDay() + 1) % 7;

// A YYYY-MM-DD key for "today" used to split upcoming vs. past events.
const todayKey = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

interface Meeting {
  day: number;
  start: number;
  end: number;
}
// A normalised class-schedule entry the weekly grid renders.
interface ScheduleSlot {
  day: number;
  start: number;
  end: number;
  title: string; // course title
  code: string;
  credits: number;
  instructor?: string | null;
}

const coerceMeetings = (raw: unknown): Meeting[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((m) => m as Record<string, unknown>)
    .filter(
      (m) =>
        Number.isFinite(m?.day) &&
        Number.isFinite(m?.start) &&
        Number.isFinite(m?.end) &&
        (m.day as number) >= 0 &&
        (m.day as number) <= 6,
    )
    .map((m) => ({ day: m.day as number, start: m.start as number, end: m.end as number }));
};

interface CalForm {
  title: string;
  category: CalCategory;
  startDate: string | null;
  endDate: string | null;
  description: string;
  audience: string;
}
const EMPTY_CAL_FORM: CalForm = {
  title: "",
  category: "academic",
  startDate: null,
  endDate: null,
  description: "",
  audience: "",
};

// ApiError carries the human reason on `displayMessage` (see api client).
const calErrMsg = (err: unknown): string => {
  const e = err as { displayMessage?: string; message?: string };
  return (
    (typeof e?.displayMessage === "string" && e.displayMessage) ||
    (typeof e?.message === "string" && e.message) ||
    "خطا رخ داد"
  );
};

export const CalendarPage: React.FC<MorePageProps> = ({ go }) => {
  void go;
  const { activeRoleId, isAuthenticated } = useRole();
  const isStaff = !!activeRoleId && STAFF_ROLES.includes(activeRoleId);
  const isInstructorLike = activeRoleId === "instructor" || activeRoleId === "ta";

  // ── institutional events ──────────────────────────────────────────────
  const [events, setEvents] = React.useState<CalEvent[]>([]);
  const [evLoading, setEvLoading] = React.useState(true);
  const [evError, setEvError] = React.useState<string | null>(null);
  const [selectedIso, setSelectedIso] = React.useState<string | null>(null);

  const loadEvents = React.useCallback(async () => {
    setEvLoading(true);
    setEvError(null);
    try {
      const rows = (await calendarApi.list({})) as CalEvent[];
      setEvents(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setEvError(calErrMsg(e));
    } finally {
      setEvLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  // ── the user's weekly class schedule ──────────────────────────────────
  const [slots, setSlots] = React.useState<ScheduleSlot[]>([]);
  const [schedTitle, setSchedTitle] = React.useState<string | null>(null);
  const [schedLoading, setSchedLoading] = React.useState(true);
  const [schedError, setSchedError] = React.useState<string | null>(null);

  const loadSchedule = React.useCallback(async () => {
    if (!isAuthenticated) {
      setSchedLoading(false);
      return;
    }
    setSchedLoading(true);
    setSchedError(null);
    try {
      const out: ScheduleSlot[] = [];
      if (isInstructorLike) {
        // Instructor/TA: their teaching load (sectionsApi.mine()).
        const rows = (await sectionsApi.mine()) as Array<{
          schedule?: unknown;
          course?: { code?: string; title?: string; credits?: number };
          instructor?: { user?: { fullName?: string } } | null;
        }>;
        for (const s of Array.isArray(rows) ? rows : []) {
          for (const m of coerceMeetings(s.schedule)) {
            out.push({
              ...m,
              title: s.course?.title ?? "—",
              code: s.course?.code ?? "",
              credits: s.course?.credits ?? 0,
            });
          }
        }
        setSchedTitle(null);
      } else {
        // Student (and any other role): their enrolled sections (registrationApi.me()).
        const reg = (await registrationApi.me()) as {
          term?: { title?: string } | null;
          items?: Array<{
            schedule?: unknown;
            course?: { code?: string; title?: string; credits?: number };
          }>;
        };
        for (const it of reg?.items ?? []) {
          for (const m of coerceMeetings(it.schedule)) {
            out.push({
              ...m,
              title: it.course?.title ?? "—",
              code: it.course?.code ?? "",
              credits: it.course?.credits ?? 0,
            });
          }
        }
        setSchedTitle(reg?.term?.title ?? null);
      }
      out.sort((a, b) => a.day - b.day || a.start - b.start);
      setSlots(out);
    } catch (e) {
      setSchedError(calErrMsg(e));
    } finally {
      setSchedLoading(false);
    }
  }, [isAuthenticated, isInstructorLike]);

  React.useEffect(() => {
    void loadSchedule();
  }, [loadSchedule]);

  const slotsByDay = React.useMemo<ScheduleSlot[][]>(() => {
    const grouped: ScheduleSlot[][] = WEEKDAYS_FA.map(() => []);
    for (const s of slots) grouped[s.day]?.push(s);
    return grouped;
  }, [slots]);
  const today = persianTodayIndex();
  const activeDays = WEEKDAYS_FA.map((_, i) => i).filter((i) => slotsByDay[i].length > 0);

  // Events mapped to the JalaliCalendar's expected shape (it does the Jalali math).
  // A multi-day event (endDate set) is expanded to ONE entry per covered day so the
  // grid shows a dot on every covered cell — not just the start day (date-only ISO
  // strings stepped literally, no Date()/UTC math to avoid timezone drift).
  const calEvents = React.useMemo(() => {
    const out: Array<{ date: string; title: string; category: string }> = [];
    const stepIso = (iso: string): string => {
      const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
      const dt = new Date(Date.UTC(y, m - 1, d + 1));
      return dt.toISOString().slice(0, 10);
    };
    for (const e of events) {
      const start = e.startDate.slice(0, 10);
      const end = (e.endDate ?? e.startDate).slice(0, 10);
      let cur = start;
      // guard against a malformed range (end before start) or runaway loops (cap 366).
      for (let i = 0; i < 366 && cur <= end; i++) {
        out.push({ date: cur, title: e.title, category: e.category });
        if (cur === end) break;
        cur = stepIso(cur);
      }
    }
    return out;
  }, [events]);

  // Events on the selected day (matched on the YYYY-MM-DD start date, plus any
  // multi-day event whose range covers the selected day).
  const selectedDayEvents = React.useMemo(() => {
    if (!selectedIso) return [];
    return events
      .filter((e) => {
        const start = e.startDate.slice(0, 10);
        const end = (e.endDate ?? e.startDate).slice(0, 10);
        return selectedIso >= start && selectedIso <= end;
      })
      .sort((a, b) => a.startDate.localeCompare(b.startDate));
  }, [events, selectedIso]);

  // Upcoming events (>= today), soonest first — the default list when no day picked.
  const upcoming = React.useMemo(() => {
    const t = todayKey();
    return events
      .filter((e) => (e.endDate ?? e.startDate).slice(0, 10) >= t)
      .sort((a, b) => a.startDate.localeCompare(b.startDate))
      .slice(0, 8);
  }, [events]);

  // ── staff create / edit / delete dialog ───────────────────────────────
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<CalEvent | null>(null);
  const [form, setForm] = React.useState<CalForm>(EMPTY_CAL_FORM);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState<CalEvent | null>(null);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const openAdd = (): void => {
    setEditing(null);
    setForm({ ...EMPTY_CAL_FORM, startDate: selectedIso });
    setFormError(null);
    setDialogOpen(true);
  };
  const openEdit = (ev: CalEvent): void => {
    setEditing(ev);
    setForm({
      title: ev.title,
      category: (CAL_CATEGORIES.includes(ev.category as CalCategory) ? ev.category : "event") as CalCategory,
      startDate: ev.startDate.slice(0, 10),
      endDate: ev.endDate ? ev.endDate.slice(0, 10) : null,
      description: ev.description ?? "",
      audience: ev.audience === "all" ? "" : ev.audience ?? "",
    });
    setFormError(null);
    setDialogOpen(true);
  };
  const closeDialog = (): void => {
    if (saving) return;
    setDialogOpen(false);
    setEditing(null);
    setForm(EMPTY_CAL_FORM);
    setFormError(null);
  };

  const submitForm = async (): Promise<void> => {
    if (saving) return;
    setFormError(null);
    if (form.title.trim().length < 2) {
      setFormError("عنوان رویداد باید حداقل ۲ نویسه باشد.");
      return;
    }
    if (!form.startDate) {
      setFormError("تاریخ شروع را انتخاب کنید.");
      return;
    }
    const start = form.startDate.slice(0, 10);
    const end = form.endDate ? form.endDate.slice(0, 10) : undefined;
    if (end && end < start) {
      setFormError("تاریخ پایان نمی‌تواند پیش از تاریخ شروع باشد.");
      return;
    }
    const body: Record<string, unknown> = {
      title: form.title.trim(),
      category: form.category,
      startDate: start,
      audience: form.audience.trim() || "all",
    };
    if (end) body.endDate = end;
    if (form.description.trim()) body.description = form.description.trim();
    setSaving(true);
    try {
      if (editing) await calendarApi.update(editing.id, body);
      else await calendarApi.create(body);
      setDialogOpen(false);
      setEditing(null);
      setForm(EMPTY_CAL_FORM);
      await loadEvents();
    } catch (e) {
      // Keep the dialog open + show the error in-dialog (never close-on-error).
      setFormError(calErrMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (): Promise<void> => {
    if (!confirmDelete || deleting) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      await calendarApi.remove(confirmDelete.id);
      setConfirmDelete(null);
      await loadEvents();
    } catch (e) {
      setDeleteError(calErrMsg(e));
    } finally {
      setDeleting(false);
    }
  };

  const rangeLabel = (ev: CalEvent): string => {
    const s = formatJalaliShort(ev.startDate);
    if (ev.endDate && ev.endDate.slice(0, 10) !== ev.startDate.slice(0, 10)) {
      return `${s} تا ${formatJalaliShort(ev.endDate)}`;
    }
    return s;
  };

  // The event list shown beside the grid: the selected day's events, or upcoming.
  const listEvents = selectedIso ? selectedDayEvents : upcoming;
  const listTitle = selectedIso
    ? `رویدادهای ${formatJalaliShort(selectedIso)}`
    : "رویدادهای پیش‌رو";

  return (
    <main data-screen-label="16 تقویم">
      <section style={{ padding: "60px 0 28px", borderBottom: "1px solid var(--line)" }}>
        <div className="shell">
          <div className="flex justify-between items-end gap-6 flex-wrap">
            <div style={{ minWidth: 0 }}>
              <span className="eyebrow">CALENDAR · تقویم</span>
              <h1 className="h-1 mt-4">تقویم</h1>
              <p className="lead mt-3.5">
                رویدادهای رسمی تقویم آموزشی و برنامهٔ هفتگی کلاس‌های شما — همه بر پایهٔ
                تقویم شمسی.
              </p>
            </div>
            {isStaff ? (
              <Button variant="primary" onClick={openAdd}>
                <Icon name="plus" size={14} />رویداد جدید
              </Button>
            ) : null}
          </div>
          <div className="mt-4">
            <HelpNote storageKey="calendar-overview" title="راهنمای تقویم">
              تقویمِ ماهانه رویدادهای رسمی (شروع و پایان دوره، آزمون‌ها، تعطیلات و مهلت‌ها) را با
              تاریخ شمسی نشان می‌دهد؛ روی هر روز بزنید تا رویدادهای آن روز را ببینید. در بخش «برنامهٔ
              هفتگی»، کلاس‌های دورهٔ جاری شما بر اساس روزهای هفته (شنبه تا جمعه) و ساعت نمایش داده
              می‌شود.{isStaff ? " به‌عنوان مدیر، می‌توانید رویداد جدید بسازید و رویدادها را ویرایش یا حذف کنید." : ""}
            </HelpNote>
          </div>
        </div>
      </section>

      {/* ── institutional events: Jalali month grid + day/upcoming list ── */}
      <section className="shell" style={{ padding: "32px 24px 16px" }}>
        <div className="flex justify-between items-baseline mb-4 flex-wrap gap-2">
          <h2 className="h-2">رویدادهای تقویم</h2>
          {selectedIso ? (
            <button type="button" className="btn btn-ghost btn-sm" style={{ minHeight: 40 }} onClick={() => setSelectedIso(null)}>
              <Icon name="x" size={13} /> نمایش رویدادهای پیش‌رو
            </button>
          ) : null}
        </div>

        {/* legend */}
        <div className="flex gap-4 flex-wrap mb-4" style={{ fontSize: 13, color: "var(--fg-mute)" }}>
          {CAL_CATEGORIES.map((c) => (
            <span key={c} className="flex items-center gap-2">
              <span style={{ width: 10, height: 10, borderRadius: 999, background: CAL_CAT_META[c].color }} />
              {CAL_CAT_META[c].label}
            </span>
          ))}
        </div>

        {evError ? (
          <div className="crud-form-error" role="alert" style={{ marginBottom: 16 }}>{evError}</div>
        ) : null}

        {evLoading ? (
          <div className="card-flat p-8 text-center" style={{ color: "var(--fg-mute)" }}>
            در حال بارگذاری رویدادها…
          </div>
        ) : (
          <div className="grid gap-6" style={{ gridTemplateColumns: "minmax(0, 1.3fr) minmax(0, 1fr)", alignItems: "start" }}>
            <JalaliCalendar
              events={calEvents}
              selectedIso={selectedIso}
              onSelectDay={(iso) => setSelectedIso((cur) => (cur === iso ? null : iso))}
              colorForCategory={calColorFor}
            />

            <div>
              <h3 className="h-3 mb-3">{listTitle}</h3>
              {listEvents.length === 0 ? (
                <div className="card-flat p-6 text-center" style={{ color: "var(--fg-mute)", fontSize: 14 }}>
                  {events.length === 0
                    ? "هنوز رویدادی در تقویم ثبت نشده است."
                    : selectedIso
                      ? "این روز رویدادی ندارد."
                      : "رویداد پیش‌رویی ثبت نشده است."}
                </div>
              ) : (
                <div className="flex flex-col gap-2.5">
                  {listEvents.map((ev) => {
                    const m = calMetaFor(ev.category);
                    return (
                      <div
                        key={ev.id}
                        className="card-flat"
                        style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: 14, borderInlineStart: `4px solid ${m.color}` }}
                      >
                        <span
                          aria-hidden
                          style={{ width: 34, height: 34, borderRadius: 10, display: "inline-flex", alignItems: "center", justifyContent: "center", background: `color-mix(in oklch, ${m.color} 14%, var(--surface))`, color: m.color, flexShrink: 0 }}
                        >
                          <Icon name={m.icon} size={17} />
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 3 }}>
                            <strong style={{ fontSize: 14.5 }}>{ev.title}</strong>
                            <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 9px", borderRadius: 999, background: `color-mix(in oklch, ${m.color} 12%, var(--surface))`, color: m.color }}>
                              {m.label}
                            </span>
                          </div>
                          <div style={{ fontSize: 11.5, color: "var(--fg-dim)", display: "flex", gap: 5, alignItems: "center" }}>
                            <Icon name="calendar" size={12} /> {rangeLabel(ev)}
                          </div>
                          {ev.description ? (
                            <p style={{ fontSize: 12.5, color: "var(--fg-mute)", lineHeight: 1.7, margin: "5px 0 0" }}>{ev.description}</p>
                          ) : null}
                        </div>
                        {isStaff ? (
                          <div className="flex gap-1" style={{ flexShrink: 0 }}>
                            <button type="button" className="btn btn-ghost btn-sm icon-btn" aria-label={"ویرایش " + ev.title} title="ویرایش" style={{ minWidth: 44, minHeight: 44 }} onClick={() => openEdit(ev)}>
                              <Icon name="edit" size={14} />
                            </button>
                            <button type="button" className="btn btn-ghost btn-sm icon-btn" aria-label={"حذف " + ev.title} title="حذف" style={{ minWidth: 44, minHeight: 44, color: "var(--rose, #e11d48)" }} onClick={() => { setConfirmDelete(ev); setDeleteError(null); }}>
                              <Icon name="trash" size={14} />
                            </button>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ── the user's REAL weekly class schedule ── */}
      <section className="shell" style={{ padding: "24px 24px 60px" }}>
        <div className="flex justify-between items-baseline mb-4 flex-wrap gap-2">
          <h2 className="h-2">برنامهٔ هفتگی{isInstructorLike ? " تدریس" : ""}</h2>
          {schedTitle ? <span className="mono" style={{ color: "var(--fg-mute)", fontSize: 12 }}>{schedTitle}</span> : null}
        </div>

        {!isAuthenticated ? (
          <div className="card-flat p-8 text-center" style={{ color: "var(--fg-mute)" }}>
            برای دیدن برنامهٔ هفتگی وارد حساب کاربری شوید.
          </div>
        ) : schedError ? (
          <div className="crud-form-error" role="alert">{schedError}</div>
        ) : schedLoading ? (
          <div className="card-flat p-8 text-center" style={{ color: "var(--fg-mute)" }}>
            در حال بارگذاری برنامهٔ هفتگی…
          </div>
        ) : activeDays.length === 0 ? (
          <div className="card-flat p-8 text-center" style={{ color: "var(--fg-mute)" }}>
            <Icon name="calendar" size={26} />
            <div className="mt-2.5" style={{ fontSize: 14 }}>
              {isInstructorLike
                ? "برای این دوره کلاسی با زمان مشخص به شما تخصیص نیافته است."
                : "برای این دوره کلاسی با زمان مشخص در برنامهٔ شما نیست. پس از نامزدسازی و ثبت‌نام، برنامهٔ هفتگی این‌جا نمایش داده می‌شود."}
            </div>
          </div>
        ) : (
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 230px), 1fr))" }}>
            {activeDays.map((di) => (
              <div
                key={di}
                className="card-flat"
                style={{ padding: 14, borderColor: di === today ? "color-mix(in oklch, var(--accent) 35%, transparent)" : undefined }}
              >
                <div className="flex items-center gap-2 mb-2.5">
                  <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{WEEKDAYS_FA[di]}</h3>
                  {di === today ? <span className="pill pill-cyan" style={{ fontSize: 10 }}>امروز</span> : null}
                  <span style={{ marginInlineStart: "auto", fontSize: 11.5, color: "var(--fg-mute)" }}>{toFa(slotsByDay[di].length)} جلسه</span>
                </div>
                <div className="flex flex-col gap-2">
                  {slotsByDay[di].map((s, i) => (
                    <div
                      key={`${s.code}-${i}`}
                      style={{ display: "flex", gap: 10, alignItems: "stretch", padding: "9px 11px", borderRadius: 10, background: "color-mix(in oklch, var(--accent) 8%, var(--surface))", border: "1px solid color-mix(in oklch, var(--accent) 22%, transparent)" }}
                    >
                      <span style={{ width: 3, borderRadius: 999, background: "var(--accent)", flexShrink: 0 }} />
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <span style={{ display: "block", fontWeight: 700, fontSize: 13.5 }}>{s.title}</span>
                        <span style={{ display: "block", fontSize: 11, color: "var(--fg-mute)", marginTop: 2 }}>
                          {s.code ? <span dir="ltr">{s.code}</span> : null}
                          {s.credits ? <> · {toFa(s.credits)} واحد</> : null}
                          {s.instructor ? <> · {s.instructor}</> : null}
                        </span>
                      </span>
                      <span dir="ltr" style={{ fontSize: 12, fontVariantNumeric: "tabular-nums", color: "var(--accent)", fontWeight: 600, alignSelf: "center", whiteSpace: "nowrap" }}>
                        {minToHHMM(s.start)}–{minToHHMM(s.end)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── staff create / edit dialog ── */}
      {dialogOpen ? (
        <ModalShell onClose={closeDialog} maxWidth={520} labelledBy="cal-form-title" cardClassName="card-flat" cardStyle={{ padding: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.6rem", padding: "1.1rem 1.2rem", borderBottom: "1px solid var(--line)" }}>
            <h2 id="cal-form-title" className="h-3" style={{ margin: 0 }}>{editing ? "ویرایش رویداد" : "افزودن رویداد"}</h2>
            <button type="button" className="btn btn-ghost btn-sm" style={{ minHeight: 44, minWidth: 44 }} onClick={closeDialog} aria-label="بستن" disabled={saving}>
              <Icon name="x" size={16} />
            </button>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); void submitForm(); }} style={{ padding: "1.2rem", display: "flex", flexDirection: "column", gap: "0.85rem" }}>
            <label style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
              <span className="crud-form-label">عنوان رویداد <span style={{ color: "var(--accent)" }}>*</span></span>
              <input className="crud-form-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="مثلاً: آغاز نامزدسازی دورهٔ دوم" style={{ minHeight: 44 }} />
            </label>

            <label style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
              <span className="crud-form-label">دسته</span>
              <select className="crud-form-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as CalCategory })} style={{ minHeight: 44 }}>
                {CAL_CATEGORIES.map((c) => <option key={c} value={c}>{CAL_CAT_META[c].label}</option>)}
              </select>
            </label>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "0.7rem" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                <span className="crud-form-label">تاریخ شروع <span style={{ color: "var(--accent)" }}>*</span></span>
                <JalaliDatePicker value={form.startDate} onChange={(iso) => setForm({ ...form, startDate: iso ? iso.slice(0, 10) : null })} ariaLabel="تاریخ شروع" />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                <span className="crud-form-label">تاریخ پایان (اختیاری)</span>
                <JalaliDatePicker value={form.endDate} onChange={(iso) => setForm({ ...form, endDate: iso ? iso.slice(0, 10) : null })} ariaLabel="تاریخ پایان" placeholder="بدون پایان" />
              </label>
            </div>

            <label style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
              <span className="crud-form-label">مخاطب (اختیاری)</span>
              <input className="crud-form-input" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} placeholder="مثلاً: همهٔ شرکت‌ها" style={{ minHeight: 44 }} />
            </label>

            <label style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
              <span className="crud-form-label">توضیحات (اختیاری)</span>
              <textarea className="crud-form-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="جزئیات رویداد…" rows={3} style={{ resize: "vertical", minHeight: 80 }} />
            </label>

            {formError ? <div className="crud-form-error" role="alert">{formError}</div> : null}

            <div style={{ display: "flex", gap: "0.6rem", paddingTop: "0.3rem", flexWrap: "wrap" }}>
              <button type="submit" className="btn btn-primary" style={{ flex: 1, minHeight: 46, minWidth: 140 }} disabled={saving}>
                {saving ? "در حال ذخیره…" : editing ? "ذخیرهٔ تغییرات" : "ثبت رویداد"}
              </button>
              <button type="button" className="btn btn-ghost" style={{ minHeight: 46 }} onClick={closeDialog} disabled={saving}>انصراف</button>
            </div>
          </form>
        </ModalShell>
      ) : null}

      {/* ── staff delete-confirm dialog ── */}
      {confirmDelete ? (
        <ModalShell
          onClose={() => { if (!deleting) { setConfirmDelete(null); setDeleteError(null); } }}
          maxWidth={440}
          labelledBy="cal-delete-title"
          cardClassName="card-flat"
          cardStyle={{ padding: "1.4rem" }}
        >
          <h2 id="cal-delete-title" className="h-3" style={{ margin: "0 0 0.7rem" }}>حذف رویداد</h2>
          <p style={{ margin: "0 0 1rem", fontSize: 14, color: "var(--fg-mute)", lineHeight: 1.8 }}>
            آیا از حذف رویداد «{confirmDelete.title}» مطمئن هستید؟ این عمل قابل بازگشت نیست.
          </p>
          {deleteError ? <div className="crud-form-error" role="alert" style={{ marginBottom: 12 }}>{deleteError}</div> : null}
          <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
            <button type="button" className="btn btn-primary" style={{ flex: 1, minHeight: 46, minWidth: 120, background: "var(--rose, #e11d48)", borderColor: "var(--rose, #e11d48)" }} disabled={deleting} onClick={() => void doDelete()}>
              {deleting ? "در حال حذف…" : "حذف"}
            </button>
            <button type="button" className="btn btn-ghost" style={{ minHeight: 46 }} disabled={deleting} onClick={() => { setConfirmDelete(null); setDeleteError(null); }}>انصراف</button>
          </div>
        </ModalShell>
      ) : null}
    </main>
  );
};

// =====================================================
// Library — Resources (R-LIB, REAL)
// =====================================================
// The mock LIBRARY_RESOURCES/LIBRARY_ITEMS source is GONE. This page reads the
// real /v1/library backend: browse is open to any authed user (published rows
// only; managers also see drafts), and content_manager/admin/super_admin get
// «افزودن منبع» + per-row ویرایش/حذف via ModalShell dialogs (errors in-dialog,
// success closes + refetches). v1 = URL/reference-based resources (each card
// links out to its url).
// =====================================================

const LIBRARY_TYPES = [
  "guide",
  "template",
  "video",
  "link",
  "document",
  "book",
] as const;
type LibraryTypeId = (typeof LIBRARY_TYPES)[number];

// Persian label + sidebar icon per type (single source of truth).
const TYPE_META: Record<LibraryTypeId, { label: string; icon: string }> = {
  guide: { label: "راهنما", icon: "book" },
  template: { label: "قالب", icon: "layers" },
  video: { label: "ویدئو", icon: "video" },
  link: { label: "پیوند", icon: "link" },
  document: { label: "سند", icon: "file" },
  book: { label: "کتاب", icon: "book" },
};

const typeLabel = (t: string): string =>
  (TYPE_META[t as LibraryTypeId]?.label ?? t).toString();

// Short uppercase badge shown on the resource thumbnail.
const typeBadge = (t: string): string => {
  switch (t) {
    case "video":
      return "ویدئو";
    case "book":
      return "کتاب";
    case "document":
      return "سند";
    case "template":
      return "قالب";
    case "guide":
      return "راهنما";
    default:
      return "پیوند";
  }
};

// Thumbnail tint per type (reuses the existing soft tokens).
const typeColors = (t: string): { bg: string; fg: string } => {
  switch (t) {
    case "video":
      return { bg: "var(--accent-soft)", fg: "var(--accent)" };
    case "book":
    case "document":
      return { bg: "var(--gold-soft)", fg: "var(--gold)" };
    case "template":
    case "guide":
      return { bg: "var(--sage-soft)", fg: "var(--sage)" };
    default:
      return { bg: "var(--navy-soft)", fg: "var(--navy)" };
  }
};

interface LibResource {
  id: string;
  type: string;
  title: string;
  author: string | null;
  description: string | null;
  url: string;
  tag: string | null;
  language: string | null;
  level: string | null;
  isPublished: boolean;
  createdAt: string;
}

// The editable form's shape (strings everywhere — selects/inputs).
interface LibForm {
  type: LibraryTypeId;
  title: string;
  url: string;
  author: string;
  description: string;
  tag: string;
  language: string;
  level: string;
  isPublished: boolean;
}

const EMPTY_FORM: LibForm = {
  type: "link",
  title: "",
  url: "",
  author: "",
  description: "",
  tag: "",
  language: "",
  level: "",
  isPublished: true,
};

// ApiError carries the human reason on `displayMessage` (see api client) — fall
// back to a generic Persian message otherwise.
const errMsg = (err: unknown): string => {
  const e = err as { displayMessage?: string; message?: string };
  return (
    (typeof e?.displayMessage === "string" && e.displayMessage) ||
    (typeof e?.message === "string" && e.message) ||
    "خطا رخ داد"
  );
};

const MANAGER_ROLES: ReadonlyArray<string> = ["content_manager", "admin", "super_admin"];

export const LibraryPage: React.FC<MorePageProps> = ({ go }) => {
  void go;
  const { activeRoleId } = useRole();
  const isManager = !!activeRoleId && MANAGER_ROLES.includes(activeRoleId);

  const [items, setItems] = React.useState<LibResource[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const [activeCat, setActiveCat] = React.useState<"all" | LibraryTypeId>("all");
  const [query, setQuery] = React.useState("");

  // dialog state: null = closed; "new" = create; else = edit that id.
  const [dialog, setDialog] = React.useState<"new" | "edit" | null>(null);
  const [editId, setEditId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<LibForm>(EMPTY_FORM);
  const [saving, setSaving] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  // delete-confirm state.
  const [deleteId, setDeleteId] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = (await libraryApi.list({})) as LibResource[];
      setItems(rows);
    } catch (e) {
      setLoadError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  // Category counts from the real fetched data.
  const counts = React.useMemo(() => {
    const m: Record<string, number> = {};
    for (const it of items) m[it.type] = (m[it.type] ?? 0) + 1;
    return m;
  }, [items]);

  const cats: { id: "all" | LibraryTypeId; t: string; n: number; ic: string }[] = [
    { id: "all", t: "همه", n: items.length, ic: "folder" },
    ...LIBRARY_TYPES.map((t) => ({
      id: t,
      t: TYPE_META[t].label,
      n: counts[t] ?? 0,
      ic: TYPE_META[t].icon,
    })),
  ];

  const filteredItems = items.filter((it) => {
    if (activeCat !== "all" && it.type !== activeCat) return false;
    if (query) {
      const q = query.toLowerCase();
      const hay = (
        it.title +
        " " +
        (it.author ?? "") +
        " " +
        (it.description ?? "") +
        " " +
        (it.tag ?? "")
      ).toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const openCreate = (): void => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setFormError(null);
    setDialog("new");
  };

  const openEdit = (it: LibResource): void => {
    setForm({
      type: (LIBRARY_TYPES.includes(it.type as LibraryTypeId) ? it.type : "link") as LibraryTypeId,
      title: it.title,
      url: it.url,
      author: it.author ?? "",
      description: it.description ?? "",
      tag: it.tag ?? "",
      language: it.language ?? "",
      level: it.level ?? "",
      isPublished: it.isPublished,
    });
    setEditId(it.id);
    setFormError(null);
    setDialog("edit");
  };

  const closeDialog = (): void => {
    if (saving) return;
    setDialog(null);
    setEditId(null);
    setFormError(null);
  };

  const submitForm = async (): Promise<void> => {
    setFormError(null);
    if (!form.title.trim()) {
      setFormError("عنوان منبع را وارد کنید.");
      return;
    }
    if (!form.url.trim()) {
      setFormError("نشانی (URL) منبع را وارد کنید.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        type: form.type,
        title: form.title.trim(),
        url: form.url.trim(),
        author: form.author.trim() || undefined,
        description: form.description.trim() || undefined,
        tag: form.tag.trim() || undefined,
        language: form.language.trim() || undefined,
        level: form.level.trim() || undefined,
        isPublished: form.isPublished,
      };
      if (dialog === "edit" && editId) {
        await libraryApi.update(editId, body);
      } else {
        await libraryApi.create(body);
      }
      setDialog(null);
      setEditId(null);
      await load();
    } catch (e) {
      // Keep the dialog open + show the error in-dialog (never close-on-error).
      setFormError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleteId) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      await libraryApi.remove(deleteId);
      setDeleteId(null);
      await load();
    } catch (e) {
      setDeleteError(errMsg(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <main data-screen-label="17 کتابخانه">
      <section style={{ padding: "60px 0 32px", borderBottom: "1px solid var(--line)" }}>
        <div className="shell">
          <div className="flex justify-between items-end gap-6 flex-wrap">
            <div>
              <span className="eyebrow">DIGITAL LIBRARY · کتابخانه‌ی دیجیتال</span>
              <h1 className="h-1 mt-4">کتابخانه‌ی دیجیتال</h1>
              <p className="lead mt-3.5">
                منابع منتخب آموزشی — مقاله، کتاب، ویدئو، قالب، راهنما و پیوند. هر منبع به نشانی اصلیِ خود ارجاع می‌دهد و از طریق جستجو و دسته‌بندی در دسترس است.
              </p>
            </div>
            {isManager ? (
              <Button variant="primary" onClick={openCreate}>
                <Icon name="plus" size={14} />افزودن منبع
              </Button>
            ) : null}
          </div>
          {isManager ? (
            <div className="mt-4">
              <HelpNote storageKey="library-manager" title="مدیریت کتابخانه">
                به‌عنوان مدیر محتوا می‌توانید منبع تازه اضافه کنید: عنوان، نشانی (URL) و نوع منبع الزامی است؛ نویسنده، توضیح، برچسب، زبان و سطح اختیاری‌اند. منابعِ «پیش‌نویس» فقط برای مدیران دیده می‌شوند؛ با فعال‌کردن «منتشرشده» منبع برای همهٔ کاربران نمایش داده می‌شود.
              </HelpNote>
            </div>
          ) : null}
        </div>
      </section>

      <section className="shell" style={{ padding: "40px 40px" }}>
        {loadError ? (
          <div className="crud-form-error" role="alert" style={{ marginBottom: 16 }}>
            {loadError}
          </div>
        ) : null}

        <div className="svc-2col lib-shell">
          <aside className="sticky" style={{ top: 90, alignSelf: "start" }}>
            <div className="mono mb-3" style={{ color: "var(--fg-mute)", fontSize: 11, letterSpacing: "0.1em" }}>دسته‌ها</div>
            <ul className="p-0 m-0 flex flex-col gap-0.5" style={{ listStyle: "none" }}>
              {cats.map((c) => (
                <li key={c.id}>
                  <button className="flex items-center justify-between rounded-lg cursor-pointer"
                    onClick={() => setActiveCat(c.id)}
                    aria-pressed={activeCat === c.id}
                    style={{ width: "100%",
                      padding: "10px 14px",
                      background: activeCat === c.id ? "var(--accent-soft)" : "transparent",
                      border: "1px solid " + (activeCat === c.id ? "color-mix(in oklch, var(--accent) 25%, transparent)" : "transparent"),
                      color: activeCat === c.id ? "var(--accent)" : "var(--fg-mute)",
                      fontFamily: "inherit", fontSize: 14 }}>
                    <span className="flex items-center gap-2.5"><Icon name={c.ic} size={14} />{c.t}</span>
                    <span className="mono" style={{ fontSize: 11 }}>{toFa(c.n)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <div>
            <div className="search-bar" style={{ margin: "0 0 32px", maxWidth: "100%" }}>
              <Icon name="search" size={18} />
              <input
                placeholder="جستجو در کتابخانه — عنوان، نویسنده، توضیح، برچسب"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="جستجو در کتابخانه"
              />
            </div>

            <div className="flex justify-between items-baseline mb-4.5">
              <h3 className="h-3">جدیدترین منابع</h3>
              <span className="mono" style={{ color: "var(--fg-mute)", fontSize: 11 }}>{toFa(filteredItems.length)} نتیجه</span>
            </div>

            <div className="flex flex-col gap-2.5">
              {loading ? (
                <div className="card-flat p-8 text-center" style={{ color: "var(--fg-mute)" }}>
                  در حال بارگذاری منابع…
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="card-flat p-8 text-center" style={{ color: "var(--fg-mute)" }}>
                  <Icon name="search" size={28} />
                  <div className="mt-2.5" style={{ fontSize: 14 }}>
                    {items.length === 0
                      ? "هنوز منبعی در کتابخانه ثبت نشده است."
                      : "منبعی با این جستجو پیدا نشد."}
                  </div>
                </div>
              ) : (
                filteredItems.map((it) => {
                  const col = typeColors(it.type);
                  return (
                    <div key={it.id} className="card-flat grid gap-4.5 items-center p-4.5"
                      style={{ gridTemplateColumns: "60px 1fr auto auto" }}>
                      <div className="rounded grid"
                        style={{ width: 48, height: 60, background: col.bg, color: col.fg, placeItems: "center",
                          fontFamily: "var(--f-mono)", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textAlign: "center", padding: "0 2px" }}>
                        {typeBadge(it.type)}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div className="mb-1 flex items-center gap-2" style={{ fontSize: 15, fontWeight: 500 }}>
                          <span>{it.title}</span>
                          {isManager && !it.isPublished ? (
                            <span className="pill" style={{ fontSize: 9, color: "var(--gold)", background: "var(--gold-soft)" }}>پیش‌نویس</span>
                          ) : null}
                        </div>
                        <div className="flex gap-3.5 flex-wrap" style={{ fontSize: 12, color: "var(--fg-mute)", fontFamily: "var(--f-mono)" }}>
                          <span>{typeLabel(it.type)}</span>
                          {it.author ? (<><span>·</span><span>{it.author}</span></>) : null}
                          {it.language ? (<><span>·</span><span>{it.language}</span></>) : null}
                          {it.level ? (<><span>·</span><span>{it.level}</span></>) : null}
                        </div>
                        {it.description ? (
                          <div className="mt-1.5" style={{ fontSize: 12.5, color: "var(--fg-mute)", lineHeight: 1.6 }}>
                            {it.description}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex gap-1.5">
                        {it.tag ? <span className="pill" style={{ fontSize: 9 }}>{it.tag}</span> : null}
                      </div>
                      <div className="flex gap-1.5">
                        {/* R-BOOKMARK — save this library resource to «ذخیره‌ها». */}
                        <BookmarkButton
                          resourceType="library"
                          resourceId={it.id}
                          title={it.title}
                          targetHref={it.url}
                          className="btn btn-outline btn-sm icon-btn"
                        />
                        <a
                          className="btn btn-outline btn-sm icon-btn"
                          href={it.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={"باز کردن " + it.title}
                          title="باز کردن منبع"
                        >
                          <Icon name="link" size={13} />
                        </a>
                        {isManager ? (
                          <>
                            <Button variant="ghost" size="sm" className="icon-btn" onClick={() => openEdit(it)}
                              aria-label={"ویرایش " + it.title} title="ویرایش">
                              <Icon name="edit" size={13} />
                            </Button>
                            <Button variant="ghost" size="sm" className="icon-btn" onClick={() => { setDeleteId(it.id); setDeleteError(null); }}
                              aria-label={"حذف " + it.title} title="حذف">
                              <Icon name="trash" size={13} />
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── create / edit dialog (managers) ── */}
      {dialog ? (
        <ModalShell
          onClose={closeDialog}
          maxWidth={560}
          labelledBy="library-form-title"
          cardClassName="card-flat"
          cardStyle={{ padding: 0 }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.6rem", padding: "1.1rem 1.2rem", borderBottom: "1px solid var(--line)" }}>
            <h2 id="library-form-title" className="h-3" style={{ margin: 0 }}>
              {dialog === "edit" ? "ویرایش منبع" : "افزودن منبع"}
            </h2>
            <button type="button" className="btn btn-ghost btn-sm" style={{ minHeight: 44, minWidth: 44 }} onClick={closeDialog} aria-label="بستن" disabled={saving}>
              <Icon name="x" size={16} />
            </button>
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); void submitForm(); }}
            style={{ padding: "1.2rem", display: "flex", flexDirection: "column", gap: "0.85rem" }}
          >
            <label style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
              <span className="crud-form-label">عنوان <span style={{ color: "var(--accent)" }}>*</span></span>
              <input className="crud-form-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="مثلاً: راهنمای شروع کار با پایتون" />
            </label>

            <label style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
              <span className="crud-form-label">نشانی (URL) <span style={{ color: "var(--accent)" }}>*</span></span>
              <input className="crud-form-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" dir="ltr" />
            </label>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.7rem" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                <span className="crud-form-label">نوع منبع</span>
                <select className="crud-form-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as LibraryTypeId })}>
                  {LIBRARY_TYPES.map((t) => (
                    <option key={t} value={t}>{TYPE_META[t].label}</option>
                  ))}
                </select>
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                <span className="crud-form-label">نویسنده / منبع</span>
                <input className="crud-form-input" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} placeholder="اختیاری" />
              </label>
            </div>

            <label style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
              <span className="crud-form-label">توضیح</span>
              <textarea className="crud-form-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="اختیاری" rows={3} style={{ resize: "vertical" }} />
            </label>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.7rem" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                <span className="crud-form-label">برچسب</span>
                <input className="crud-form-input" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} placeholder="مثلاً: ML" />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                <span className="crud-form-label">زبان</span>
                <input className="crud-form-input" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} placeholder="فارسی / English" />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                <span className="crud-form-label">سطح</span>
                <input className="crud-form-input" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} placeholder="کارشناسی / ارشد" />
              </label>
            </div>

            <label className="flex items-center gap-2.5 cursor-pointer" style={{ fontSize: 14 }}>
              <input type="checkbox" checked={form.isPublished} onChange={(e) => setForm({ ...form, isPublished: e.target.checked })} style={{ accentColor: "var(--accent)" }} />
              منتشرشده (برای همهٔ کاربران دیده می‌شود)
            </label>

            {formError ? (
              <div className="crud-form-error" role="alert">{formError}</div>
            ) : null}

            <div style={{ display: "flex", gap: "0.6rem", paddingTop: "0.4rem", flexWrap: "wrap" }}>
              <button type="submit" className="btn btn-primary" style={{ flex: 1, minHeight: 46, minWidth: 140 }} disabled={saving}>
                {saving ? "در حال ذخیره…" : dialog === "edit" ? "ذخیرهٔ تغییرات" : "افزودن منبع"}
              </button>
              <button type="button" className="btn btn-ghost" style={{ minHeight: 46 }} onClick={closeDialog} disabled={saving}>
                انصراف
              </button>
            </div>
          </form>
        </ModalShell>
      ) : null}

      {/* ── delete-confirm dialog (managers) ── */}
      {deleteId ? (
        <ModalShell
          onClose={() => { if (!deleting) { setDeleteId(null); setDeleteError(null); } }}
          maxWidth={440}
          labelledBy="library-delete-title"
          cardClassName="card-flat"
          cardStyle={{ padding: "1.4rem 1.4rem" }}
        >
          <h2 id="library-delete-title" className="h-3" style={{ margin: "0 0 0.7rem" }}>حذف منبع</h2>
          <p style={{ margin: "0 0 1rem", fontSize: 14, color: "var(--fg-mute)", lineHeight: 1.8 }}>
            آیا از حذف این منبع مطمئن هستید؟ منبع از کتابخانه برداشته می‌شود.
          </p>
          {deleteError ? (
            <div className="crud-form-error" role="alert" style={{ marginBottom: 12 }}>{deleteError}</div>
          ) : null}
          <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
            <button type="button" className="btn btn-primary" style={{ flex: 1, minHeight: 46, minWidth: 120, background: "var(--rose, #ef4444)", borderColor: "var(--rose, #ef4444)" }} disabled={deleting} onClick={() => void confirmDelete()}>
              {deleting ? "در حال حذف…" : "حذف"}
            </button>
            <button type="button" className="btn btn-ghost" style={{ minHeight: 46 }} disabled={deleting} onClick={() => { setDeleteId(null); setDeleteError(null); }}>
              انصراف
            </button>
          </div>
        </ModalShell>
      ) : null}
    </main>
  );
};

// =====================================================
// Help Center / Knowledge Base
// =====================================================
export const HelpPage: React.FC<MorePageProps> = ({ go }) => (
  <main data-screen-label="18 پشتیبانی">
    <section style={{ padding: "80px 0 60px", background: "var(--surface-2)", borderBottom: "1px solid var(--line)" }}>
      <div className="shell text-center" >
        <span className="eyebrow justify-center" >HELP CENTER · ۲۴/۷</span>
        <h1 className="h-display mt-4.5"  style={{ fontSize: "clamp(36px, 5vw, 72px)"}}>
          چطور می‌توانیم کمک کنیم؟
        </h1>
        <div className="search-bar" style={{ margin: "32px auto 0" }}>
          <Icon name="search" size={18} />
          <input placeholder="جستجو در راهنماها، آیین‌نامه‌ها و سوالات متداول" />
          <span className="mono" style={{ color: "var(--fg-mute)" }}>⌘ K</span>
        </div>
      </div>
    </section>

    <section className="shell" style={{ padding: "60px 40px" }}>
      <div className="grid grid-3 mb-15" >
        {[
          { ic: "play", t: "شروع سریع", d: "اولین کلاس، اولین تمرین، اولین گواهی"},
          { ic: "user", t: "حساب کاربری", d: "ثبت‌نام، احراز هویت، تغییر اطلاعات"},
          { ic: "live", t: "کلاس برخط", d: "ورود، تنظیم میکروفون، ضبط"},
          { ic: "dollar", t: "پرداخت و کیف پول", d: "کیف پول شخصی، بازار پکیج، بودجهٔ سازمانی"},
          { ic: "cert", t: "گواهی ساعت‌محور", d: "صدور نوع اول/دوم، راستی‌آزمایی"},
          { ic: "sparkle", t: "دستیار AI", d: "نحوه استفاده، محدودیت‌ها، تنظیمات"},
        ].map((c, i) => (
          <div key={i} className="card p-7 cursor-pointer flex flex-col gap-3.5" >
            <div className="rounded-lg grid"  style={{width: 40, height: 40, background: "var(--accent-soft)", color: "var(--accent)", placeItems: "center"}}>
              <Icon name={c.ic} size={18} />
            </div>
            <h3 style={{ fontSize: 18 }}>{c.t}</h3>
            <p className="m-0"  style={{fontSize: 13, color: "var(--fg-mute)", lineHeight: 1.6}}>{c.d}</p>
            <div className="mt-1"  style={{ fontFamily: "var(--f-mono)", fontSize: 11, color: "var(--accent)", letterSpacing: "0.08em"}}>
              ۱۲ مقاله ←
            </div>
          </div>
        ))}
      </div>

      <div className="grid gap-8"  style={{ gridTemplateColumns: "1fr 380px"}}>
        <div>
          <h2 className="h-2 mb-6" >سوالات متداول</h2>
          <div className="flex flex-col gap-0" >
            {[
              ["چطور به اولین کلاس زنده‌ام بپیوندم؟", "از داشبورد، روی برنامه‌ی هفته کلیک کنید. ۱۵ دقیقه قبل از شروع، دکمه‌ی «ورود» فعال می‌شود..."],
              ["آیا گواهی این سامانه رسمی است؟", "گواهی‌ها بر مبنای نظام آموزش کارمندان دولت (ساعت + نمره) صادر و از مسیر سامانه‌ی مرکزی صلاحیت حرفه‌ای صنعت آب و برق قابل استعلام‌اند..."],
              ["دستیار AI به اطلاعات شخصی من دسترسی دارد؟", "دستیار هوشمند فقط به اطلاعات آموزشی شما (پیشرفت، تمرین، سوال) دسترسی دارد..."],
              ["در صورت قطعی اینترنت، کلاس از دست می‌رود؟", "خیر. تمام کلاس‌های زنده ضبط می‌شوند و در آرشیو شخصی شما قابل بازیابی هستند..."],
              ["هزینهٔ دوره‌ها را چه کسی پرداخت می‌کند؟", "دوره‌های ضمن خدمتِ الزامی از بودجهٔ سازمانیِ شرکت شما تأمین می‌شود؛ برای پکیج‌های اختیاری بازار می‌توانید از کیف پول شخصی استفاده کنید..."],
              ["آیا می‌توانم پروژهٔ پایان دوره را تغییر دهم؟", "تا پیش از انتخاب رسمی، می‌توانید پروژه را تغییر دهید. پس از تایید، نیاز به مجوز مدرس دارد..."],
            ].map(([q, a], i) => (
              <details className="p-0" key={i}  style={{borderTop: i > 0 ? "1px solid var(--line)" : "1px solid var(--line)", borderBottom: i === 5 ? "1px solid var(--line)" : "none"}}>
                <summary className="cursor-pointer flex justify-between items-center gap-4"  style={{padding: "20px 0", fontWeight: 500, fontSize: 16, listStyle: "none"}}>
                  <span>{q}</span>
                  <Icon name="plus" size={16} />
                </summary>
                <p className="pb-5 m-0"  style={{fontSize: 14, color: "var(--fg-mute)", lineHeight: 1.7}}>{a}</p>
              </details>
            ))}
          </div>
        </div>

        <aside className="flex flex-col gap-4" >
          <div className="card p-6" >
            <div className="flex items-center gap-2.5 mb-3.5"  style={{ color: "var(--accent)"}}>
              <Icon name="headset" size={16} />
              <span className="mono" style={{ letterSpacing: "0.08em" }}>پشتیبانی زنده</span>
            </div>
            <p style={{ fontSize: 13, color: "var(--fg-mute)", lineHeight: 1.7 }}>
              تیم پشتیبانی ۲۴/۷ آماده پاسخگویی است. زمان میانگین پاسخ: ۸ دقیقه.
            </p>
            <Button variant="primary" className="justify-center mt-3.5" style={{width: "100%"}}
              onClick={() => window.toast?.({ title: "اتصال به پشتیبانی", msg: "اپراتور ظرف چند ثانیه پاسخ می‌دهد.", kind: "info" })}
            >
              <Icon name="chat" size={14} />شروع گفتگو
            </Button>
          </div>
          <div className="card p-6" >
            <div className="mono mb-3.5"  style={{color: "var(--fg-mute)", fontSize: 11, letterSpacing: "0.08em"}}>SYSTEM STATUS</div>
            <div className="flex flex-col gap-2.5" >
              {[
                ["LMS", "operational"],
                ["کلاس زنده", "operational"],
                ["AI Tutor", "operational"],
                ["پرداخت", "operational"],
                ["ASR · فارسی", "degraded"],
              ].map(([t, s]) => (
                <div className="flex justify-between" key={t}  style={{ fontSize: 13}}>
                  <span>{t}</span>
                  <span className="flex items-center gap-1.5"  style={{ color: s === "operational" ? "var(--sage)" : "var(--gold)"}}>
                    <span style={{ width: 8, height: 8, borderRadius: 50, background: "currentColor" }}></span>
                    {s === "operational" ? "سالم" : "بررسی"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </section>
  </main>
);

// =====================================================
// Pricing / Plans
// =====================================================
// Plans / تعرفه (D-2026-06-14) — real, admin-editable published pricing.
interface PlanRow {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  audience: string | null;
  priceAmount: number | null; // Rial; 0 = رایگان, null = «تماس بگیرید»
  priceAnnualAmount: number | null; // Rial; annual price (۲ ماه رایگان)
  pricePeriod: string;
  currency: string;
  features: string[];
  highlight: boolean;
  ctaLabel: string | null;
  ctaRoute: string | null;
  // Entitlement spine (Phase B) — for the comparison table.
  aiMessageQuota: number | null;
  aiModelTier: string | null;
  channelsAllowed: string[];
  entitlements: Record<string, unknown>;
}

// AI model-tier label for the comparison table.
const TIER_LABEL: Record<string, string> = {
  basic: "پایه",
  smart: "هوشمند",
  advanced: "پیشرفته",
  custom: "سفارشی",
};

const PERIOD_LABEL: Record<string, string> = {
  month: "ماهانه",
  term: "هر دوره",
  year: "سالانه",
  once: "یک‌بار",
  custom: "",
};

// Group Rial digits with the Persian thousands separator (٬) then Persian digits.
const formatRial = (amount: number): string =>
  toFa(amount.toLocaleString("en-US").replace(/,/g, "٬"));

// ---- Comparison table (Phase B) — derived entirely from plan fields ----
const cmpYes = (v: unknown): React.ReactNode =>
  v === true ? (
    <span
      className="grid"
      style={{
        width: 18,
        height: 18,
        borderRadius: 50,
        background: "var(--accent)",
        color: "var(--accent-on)",
        placeItems: "center",
        margin: "0 auto",
      }}
    >
      <Icon name="check" size={11} stroke={3} />
    </span>
  ) : (
    <span style={{ color: "var(--fg-mute)" }}>—</span>
  );

const cmpChannels = (ch: string[]): string => {
  const msgr = (ch || []).filter((c) => c !== "in_platform");
  if (msgr.length === 0) return "فقط پلتفرم";
  if (msgr.includes("sms")) return "همه + پیامک";
  return toFa(msgr.length) + " پیام‌رسان";
};

const cmpQuota = (q: number | null): string => (q === null ? "سفارشی" : toFa(q) + " پیام");

const COMPARE_ROWS: { key: string; label: string; render: (p: PlanRow) => React.ReactNode }[] = [
  { key: "quota", label: "پیام AI ماهانه", render: (p) => cmpQuota(p.aiMessageQuota) },
  { key: "tier", label: "کیفیت مدل AI", render: (p) => TIER_LABEL[p.aiModelTier ?? "basic"] ?? "—" },
  { key: "specialist", label: "ایجنت‌های تخصصی", render: (p) => cmpYes(p.entitlements?.specialist_agents) },
  { key: "mentor", label: "منتور AI", render: (p) => cmpYes(p.entitlements?.mentor) },
  { key: "translate", label: "ترجمه‌ی زنده", render: (p) => cmpYes(p.entitlements?.live_translation) },
  { key: "cert", label: "گواهی پایان دوره", render: (p) => cmpYes(p.entitlements?.certificate) },
  { key: "channels", label: "پیام‌رسان‌ها", render: (p) => cmpChannels(p.channelsAllowed) },
];

// Purchase-trigger badges (economy-doc §5) shown under the plan cards.
const PURCHASE_TRIGGERS: string[] = [
  "آزمایش رایگان ۷ روزه (بدون کارت)",
  "گارانتی بازگشت وجه ۷ روزه",
  "پرداخت سالانه = ۲ ماه رایگان",
  "تخفیف کارکنان و گروهی",
];

export const PricingPage: React.FC<MorePageProps> = ({ go }) => {
  const { isAuthenticated, user } = useAuth();
  const [plans, setPlans] = React.useState<PlanRow[] | null>(null);
  const [mine, setMine] = React.useState<{ active: boolean; planId?: string; planName?: string; priceAmount?: number | null } | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const tenantSlug = user?.tenantSlug || "demo";

  // Cosmetic role gate (the API is the real enforcer): a staff/instructor account
  // does not buy a personal learner plan — the backend 403s and here we swap the
  // CTA for a note. Learner tiers by slug (matches the backend default).
  const STAFF_ROLES = ["instructor", "teaching_assistant", "faculty_admin", "faculty_head", "university_head", "university_advisor", "finance_manager", "content_manager", "support", "auditor", "proctor", "admin", "super_admin", "organization"];
  const roles = user?.roles ?? [];
  const isStaff = roles.some((r) => STAFF_ROLES.includes(r)) && !roles.includes("student");
  const isLearnerPlan = (p: PlanRow): boolean => p.slug === "student" || p.slug === "pro";
  const staffBlocked = (p: PlanRow): boolean => isAuthenticated && isStaff && isLearnerPlan(p);

  // S-9 — context-aware CTA. Enterprise (priceAmount null) → contact. Guest → sign
  // in with the plan stashed (resume after auth — paths 1 & 2). Signed-in →
  // /checkout, which resolves upgrade/downgrade/renew + proration server-side.
  const handleCta = (p: PlanRow): void => {
    if (p.priceAmount == null) { go(p.ctaRoute || "help"); return; }
    if (!isAuthenticated) {
      try { sessionStorage.setItem("s9_checkout_plan", p.id); } catch { /* ignore */ }
      go("login");
      return;
    }
    go("checkout", p.id);
  };

  const ctaLabelFor = (p: PlanRow): string => {
    if (p.priceAmount == null) return p.ctaLabel || "تماس با فروش";
    if (staffBlocked(p)) return "ویژهٔ فراگیران";
    if (isAuthenticated && mine?.active) {
      if (mine.planId === p.id) return "پلن فعلی شما";
      const cur = mine.priceAmount ?? 0;
      if (p.priceAmount > cur) return "ارتقا به این پلن";
      if (p.priceAmount < cur) return "تنزل به این پلن";
      return "تغییر به این پلن";
    }
    return p.ctaLabel || "انتخاب پلن";
  };

  React.useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = (await plansApi.public(tenantSlug)) as PlanRow[];
        if (live) setPlans(d);
      } catch (e) {
        if (live) setError(e instanceof Error ? e.message : "بارگذاری پلن‌ها با خطا مواجه شد");
      } finally {
        if (live) setLoading(false);
      }
      if (isAuthenticated) {
        try {
          const m = (await subscriptionsApi.mine()) as { active: boolean; planId?: string; planName?: string; priceAmount?: number | null };
          if (live) setMine(m);
        } catch {
          if (live) setMine(null);
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [isAuthenticated, tenantSlug]);

  const list = plans ?? [];

  return (
    <main data-screen-label="پلن‌ها و قیمت">
      <section style={{ padding: "72px 0 28px", borderBottom: "1px solid var(--line)" }}>
        <div className="shell text-center">
          <span className="eyebrow justify-center">PRICING · شفاف و منصفانه</span>
          <h1 className="h-display mt-4.5" style={{ fontSize: "clamp(34px, 5vw, 64px)" }}>
            پلن مناسب خود را<br />
            <span style={{ color: "var(--accent)" }}>انتخاب کنید</span>
          </h1>
          <p className="lead" style={{ margin: "20px auto 0", maxWidth: 640 }}>
            قیمت‌ها به ریال و شامل دستیار هوشمند، کلاس زنده، آرشیو ضبط و گواهی دیجیتال است.
          </p>
        </div>
      </section>

      <section className="shell" style={{ padding: "32px 24px 60px" }}>
        <HelpNote storageKey="pricing" title="راهنمای پلن‌ها">
          هر پلن امکانات و قیمت مشخصی دارد. پلنِ «محبوب‌ترین» برای بیشتر فراگیران مناسب است. برای پلن
          سازمانی قیمت بر اساس نیاز شما تعیین می‌شود — روی «تماس» بزنید.
        </HelpNote>

        {isAuthenticated && mine?.active ? (
          <div
            className="card-flat"
            style={{ padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
          >
            <span style={{ fontSize: 13 }}>
              پلن فعلی شما: <strong style={{ color: "var(--accent)" }}>{mine.planName}</strong>
            </span>
            <span className="text-mute" style={{ fontSize: 12.5 }}>— برای ارتقا، تنزل، تمدید یا لغو، پلن دلخواه را انتخاب کنید.</span>
          </div>
        ) : null}

        {error ? (
          <div className="crud-form-error" role="alert" style={{ marginBottom: 12 }}>
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="admin-empty">در حال بارگذاری پلن‌ها…</div>
        ) : list.length === 0 ? (
          <div className="card-flat p-8 text-center" style={{ color: "var(--fg-mute)" }}>
            هنوز پلنی تعریف نشده است.
          </div>
        ) : (
          <div className="grid grid-3">
            {list.map((p) => {
              const periodLabel = PERIOD_LABEL[p.pricePeriod] ?? "";
              return (
                <div
                  key={p.id}
                  className="card p-9 relative"
                  style={{ border: p.highlight ? "2px solid var(--accent)" : "1px solid var(--line)" }}
                >
                  {p.highlight && (
                    <div
                      className="absolute rounded-full"
                      style={{
                        top: -12,
                        right: 24,
                        background: "var(--accent)",
                        color: "var(--accent-on)",
                        padding: "4px 12px",
                        fontSize: 11,
                        fontFamily: "var(--f-mono)",
                        letterSpacing: "0.08em",
                        fontWeight: 600,
                      }}
                    >
                      محبوب‌ترین
                    </div>
                  )}
                  {p.tagline ? (
                    <div className="mono" style={{ color: "var(--fg-mute)", fontSize: 11, letterSpacing: "0.1em" }}>
                      {p.tagline}
                    </div>
                  ) : null}
                  <h3 className="mt-2" style={{ fontSize: 28 }}>{p.name}</h3>
                  {p.audience ? (
                    <div style={{ fontSize: 12.5, color: "var(--fg-mute)", marginTop: 4 }}>{p.audience}</div>
                  ) : null}
                  <div className="flex items-baseline gap-1.5 mt-4 mb-6" style={{ flexWrap: "wrap" }}>
                    {p.priceAmount === null ? (
                      <span style={{ fontFamily: "var(--f-display)", fontSize: 34, fontWeight: 800 }}>تماس بگیرید</span>
                    ) : p.priceAmount === 0 ? (
                      <span style={{ fontFamily: "var(--f-display)", fontSize: 44, fontWeight: 800 }}>رایگان</span>
                    ) : (
                      <>
                        <span style={{ fontFamily: "var(--f-display)", fontSize: 40, fontWeight: 800, letterSpacing: "-0.02em" }}>
                          {formatRial(p.priceAmount)}
                        </span>
                        <span style={{ fontSize: 13, color: "var(--fg-mute)" }}>
                          ریال{periodLabel ? " / " + periodLabel : ""}
                        </span>
                      </>
                    )}
                  </div>
                  {p.priceAnnualAmount && p.priceAmount ? (
                    <div
                      style={{ fontSize: 12.5, color: "var(--fg-mute)", marginTop: -16, marginBottom: 18 }}
                    >
                      یا سالانه{" "}
                      <strong style={{ color: "var(--fg)" }}>{formatRial(p.priceAnnualAmount)} ریال</strong>
                      {" — "}
                      <span style={{ color: "var(--accent)", fontWeight: 600 }}>۲ ماه رایگان</span>
                    </div>
                  ) : null}
                  <button
                    className={"justify-center mb-7 btn " + ((mine?.active && mine.planId === p.id) || p.highlight ? "btn-primary" : "btn-outline")}
                    style={{ width: "100%" }}
                    disabled={staffBlocked(p)}
                    title={staffBlocked(p) ? "این پلن ویژهٔ فراگیران است؛ برای نقش شما مسیر جداگانه‌ای در نظر گرفته شده است" : undefined}
                    onClick={() => handleCta(p)}
                  >
                    {ctaLabelFor(p)}
                  </button>
                  <ul className="p-0 m-0 flex flex-col gap-3" style={{ listStyle: "none" }}>
                    {p.features.map((t, i) => (
                      <li className="flex items-center gap-2.5" key={i} style={{ fontSize: 13, color: "var(--fg)" }}>
                        <span
                          className="grid"
                          style={{ width: 18, height: 18, borderRadius: 50, background: "var(--accent)", color: "var(--accent-on)", placeItems: "center", flexShrink: 0 }}
                        >
                          <Icon name="check" size={11} stroke={3} />
                        </span>
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        {!loading && list.length > 0 ? (
          <>
            {/* Purchase triggers (economy-doc §5) */}
            <div
              className="flex flex-wrap items-center justify-center gap-3"
              style={{ marginTop: 36 }}
            >
              {PURCHASE_TRIGGERS.map((t) => (
                <span
                  key={t}
                  className="flex items-center gap-2"
                  style={{
                    fontSize: 12.5,
                    color: "var(--fg)",
                    background: "var(--surface-1)",
                    border: "1px solid var(--line)",
                    borderRadius: 999,
                    padding: "6px 14px",
                  }}
                >
                  <span
                    className="grid"
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 50,
                      background: "var(--accent)",
                      color: "var(--accent-on)",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon name="check" size={10} stroke={3} />
                  </span>
                  {t}
                </span>
              ))}
            </div>

            {/* Comparison table (economy-doc §2) */}
            <div style={{ marginTop: 48, overflowX: "auto" }}>
              <h2
                className="h-display text-center"
                style={{ fontSize: 24, marginBottom: 16 }}
              >
                مقایسه‌ی پلن‌ها
              </h2>
              <table
                style={{
                  width: "100%",
                  minWidth: 560,
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: "right",
                        padding: "10px 12px",
                        borderBottom: "2px solid var(--line)",
                        color: "var(--fg-mute)",
                        fontWeight: 600,
                      }}
                    >
                      امکانات
                    </th>
                    {list.map((p) => (
                      <th
                        key={p.id}
                        style={{
                          textAlign: "center",
                          padding: "10px 12px",
                          borderBottom: "2px solid var(--line)",
                          color: p.highlight ? "var(--accent)" : "var(--fg)",
                          fontWeight: 700,
                        }}
                      >
                        {p.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARE_ROWS.map((row) => (
                    <tr key={row.key}>
                      <td
                        style={{
                          textAlign: "right",
                          padding: "10px 12px",
                          borderBottom: "1px solid var(--line)",
                          color: "var(--fg-mute)",
                        }}
                      >
                        {row.label}
                      </td>
                      {list.map((p) => (
                        <td
                          key={p.id}
                          style={{
                            textAlign: "center",
                            padding: "10px 12px",
                            borderBottom: "1px solid var(--line)",
                            color: "var(--fg)",
                          }}
                        >
                          {row.render(p)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </section>
    </main>
  );
};

// =====================================================
// هیئت علمی — public faculty directory (REAL, D-2026-06-14)
// Each university (tenant) and دانشکده has its هیئت علمی: this page lists the
// real Instructor records (rank + department→بخش→دانشکده + expertise + course
// count) from GET /v1/instructors/public, grouped/filterable by دانشکده. No
// fabricated names/metrics — the prior 94-mock directory was removed.
// =====================================================
interface FacultyMember {
  id: string;
  // R-CONNECT — the member's User.id, needed for the public-profile link
  // (PersonLink). The current /v1/instructors/public payload returns the
  // Instructor.id (`id`) but NOT userId; until the backend adds `userId` to its
  // select, PersonLink degrades to plain text here (no dead link). See the
  // backend-select note returned with this change.
  userId?: string | null;
  fullName: string;
  rankLabel: string;
  status: string;
  expertise: string[];
  departmentName: string | null;
  facultyName: string | null;
  schoolId: string | null;
  schoolName: string;
  courseCount: number;
}
interface FacultyDirectory {
  total: number;
  members: FacultyMember[];
  schools: { id: string; name: string; count: number }[];
}


export const FacultyPage: React.FC<MorePageProps> = ({ go }) => {
  void go;
  const [data, setData] = React.useState<FacultyDirectory | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [activeSchool, setActiveSchool] = React.useState<string>("همه");

  React.useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = (await facultyDirectoryApi.list("demo")) as FacultyDirectory;
        if (live) setData(d);
      } catch (e) {
        if (live) setError(e instanceof Error ? e.message : "بارگذاری مدرسان با خطا مواجه شد");
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const members = data?.members ?? [];
  const schools = data?.schools ?? [];
  const shown =
    activeSchool === "همه" ? members : members.filter((m) => (m.schoolId ?? "—") === activeSchool);

  return (
    <main data-screen-label="هیئت علمی">
      <section style={{ padding: "72px 0 28px", borderBottom: "1px solid var(--line)" }}>
        <div className="shell">
          <span className="eyebrow">INSTRUCTORS · مدرسان</span>
          <h1 className="h-1 mt-4">مدرسان سامانه</h1>
          <p className="lead mt-3.5">
            {loading
              ? "در حال بارگذاری مدرسان…"
              : `${toFa(data?.total ?? 0)} مدرس در ${toFa(schools.length)} حوزهٔ آموزشی — مرتبهٔ تخصصی، گروه آموزشی و تخصص هر مدرس از سوابق واقعی نمایش داده می‌شود.`}
          </p>
        </div>
      </section>

      <section className="shell" style={{ padding: "28px 24px 60px" }}>
        <HelpNote storageKey="faculty-directory" title="راهنمای مدرسان">
          مدرسان بر اساس حوزهٔ آموزشی گروه‌بندی شده‌اند. روی نام هر حوزه بزنید تا تنها مدرسان آن
          نمایش داده شوند. مرتبهٔ تخصصی، گروه آموزشی، حوزهٔ تخصص و تعداد
          دوره‌های هر مدرس از داده‌های واقعی سامانه خوانده می‌شود.
        </HelpNote>

        {error ? (
          <div className="crud-form-error" role="alert" style={{ marginBottom: 12 }}>
            {error}
          </div>
        ) : null}

        {!loading && schools.length > 0 ? (
          <div className="flex gap-2.5 mb-8 flex-wrap">
            <button
              onClick={() => setActiveSchool("همه")}
              className={"pill filter-pill " + (activeSchool === "همه" ? "active" : "")}
              aria-pressed={activeSchool === "همه"}
              style={{ border: "none", background: activeSchool === "همه" ? "var(--accent-soft)" : "var(--surface)", padding: "6px 12px" }}
            >
              همه ({toFa(members.length)})
            </button>
            {schools.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveSchool(s.id)}
                className={"pill filter-pill " + (activeSchool === s.id ? "active" : "")}
                aria-pressed={activeSchool === s.id}
                style={{ border: "none", background: activeSchool === s.id ? "var(--accent-soft)" : "var(--surface)", padding: "6px 12px" }}
              >
                {s.name} ({toFa(s.count)})
              </button>
            ))}
          </div>
        ) : null}

        {loading ? (
          <div className="admin-empty">در حال بارگذاری…</div>
        ) : members.length === 0 ? (
          <div className="card-flat p-8 text-center" style={{ color: "var(--fg-mute)" }}>
            هنوز مدرسی ثبت نشده است. مدیر می‌تواند از بخش «مدرسان» اعضا را اضافه کند.
          </div>
        ) : (
          <div className="grid grid-3">
            {shown.length === 0 && (
              <div className="card-flat p-8 text-center" style={{ color: "var(--fg-mute)", gridColumn: "1 / -1" }}>
                مدرسی در این حوزه پیدا نشد.
              </div>
            )}
            {shown.map((m) => (
              <div key={m.id} className="card p-7 flex flex-col gap-3.5">
                <div className="flex items-center gap-3.5">
                  {/* R-CONNECT — name + avatar link to the member's public profile
                      (when userId is present; degrades to plain text otherwise). */}
                  <PersonLink
                    userId={m.userId}
                    name={m.fullName}
                    role="instructor"
                    size={56}
                    subtitle={m.rankLabel + (m.departmentName ? " · " + m.departmentName : "")}
                  />
                </div>
                <div style={{ fontSize: 12, color: "var(--fg-mute)" }}>{m.schoolName}</div>
                {m.expertise.length > 0 ? (
                  <div className="flex gap-1.5 flex-wrap mt-1">
                    {m.expertise.slice(0, 6).map((t) => (
                      <span key={t} className="pill" style={{ fontSize: 9 }}>{t}</span>
                    ))}
                  </div>
                ) : null}
                <div className="flex justify-between pt-3.5" style={{ borderTop: "1px solid var(--line)", fontFamily: "var(--f-mono)", fontSize: 11, color: "var(--fg-mute)" }}>
                  <span>{toFa(m.courseCount)} درس ارائه‌شده</span>
                  {m.status === "ON_SABBATICAL" ? <span style={{ color: "var(--amber, #b8860b)" }}>فرصت مطالعاتی</span> : <span>فعال</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

export default CalendarPage;
